import { useState, useRef, ChangeEvent, useMemo, useCallback } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { getMessage } from '@tcc/i18n';
import { Button, ButtonFill, ButtonWidth, ComponentColor, ComponentPadding, ComponentSize, Container, DensitySpacing, Dialog, DialogContent, DialogFooter, DialogWidth, FdsIcon, Icon, Orientation, Typography, TypographyVariant } from '@tcc/fds';
import { difference } from '../../../../utils/arrayUtils';
import styles from '../RegionNoteForm.css';
import {
  reqNewActivity,
  setSelectedActivity,
  reqUpdateActivity,
  reqDeleteAsset,
  reqDeleteComments,
  startDeleteComments,
} from '../../../../actions/scoutingActions';
import {
  ASSET_ACCEPTED_TYPES,
} from '../../../../constants/scouting';
import { getCurrentSeasonYear } from '../../../../selectors/seasonSelectors';
import { mapTags } from '../../../../utils/regionUtils';
import {
  closeModal,
  openModal,
  resizeModal,
} from '../../../../actions/modalActions';
import { REGION_REPORT } from '../../../../constants/modal';
import { trackEvent } from '../../../../actions/analyticsActions';
import { Activity, ActivityColor, ActivityComment, EnhancedActivity, FieldFeature, ModalMode, ModalSize } from '../../../../types';
import { useAppDispatch, useAppSelector } from '../../../../utils';
import { getActivitySeasonYear } from '../../../../utils/scoutingUtils';

import { RegionNoteFormTags } from '../RegionNoteFormTags/RegionNoteFormTags';
import { RegionNoteFormTitle } from '../RegionNoteFormTitle/RegionNoteFormTitle';
import { RegionNoteFormComment } from '../RegionNoteFormComment/RegionNoteFormComment';
import { RegionNoteFormFile } from '../RegionNoteFormFile/RegionNoteFormFile';
import { RegionNoteFormType } from '../RegionNoteFormType/RegionNoteFormType';
import { RegionNoteFormColor } from '../RegionNoteFormColor/RegionNoteFormColor';
import { RegionNoteFormDate } from '../RegionNoteFormDate/RegionNoteFormDate';
import { RegionNoteFormFooter } from '../RegionNoteFormFooter/RegionNoteFormFooter';
import { DeleteConfirmationModal } from '../confirmationModel/DeleteConfirmationModal';
import { CloseConfirmationModal } from '../confirmationModel/CloseConfirmationModal';
import { RemovePhotoConfirmationModal } from '../confirmationModel/RemovePhotoConfirmationModal';

type RegionNoteFormProps = {
  index: number;
};

type RegionNoteFormState = {
  id?: string,
  title: string,
  note: string,
  permanent: boolean,
  color: ActivityColor,
  date: string,
  selectedTags: string[],
  assetLoaded: boolean,
  assetDeleted: boolean,
  file: File | null,
  incorrectFileFormat: boolean,
};

type RegionNoteInitialValues = Pick<RegionNoteFormState, 'title' | 'note' | 'permanent' | 'color' | 'date' | 'id'> & {
  tags: string[];
}

const getInitialValues = (activity?: Activity): RegionNoteInitialValues => ({
  id: activity?.id,
  title: activity?.title ?? '',
  note:
    activity?.comments && activity.comments.length > 0
      ? activity.comments[0].note
      : '',
  permanent: activity?.permanent ?? false,
  color: activity?.color ?? ActivityColor.BLUE,
  date: activity?.scoutedAt ?? '',
  tags: activity?.tags ?? [],
});

export const RegionNoteForm = ({ index }: RegionNoteFormProps) => {
  const dispatch = useAppDispatch();
  const { activity: untypedActivity, mode: modalMode, region } = useAppSelector(state => state.modal.meta);
  const activity = untypedActivity as Activity | EnhancedActivity;

  const {
    tags: tagOptions,
    isLoadingActivity,
    isLoadingUpdatedActivity
  } = useAppSelector(state => state.scouting);
  const field = useAppSelector(state => state.field);
  const currentSeasonYear = useAppSelector(state => getCurrentSeasonYear(state, { index }));
  const wrapperRef = useRef(null);
  const fieldHasScoutingAddPhotoFeature = useAppSelector(state => state.field.features.includes(FieldFeature.SCOUTING_ADD_PHOTO));
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState<boolean>(false);
  const [closeConfirmationModal, setCloseConfirmationModal] = useState<boolean>(false);
  const [removePhotoConfirmationModal, setRemovePhotoConfirmationModal] = useState<boolean>(false);

  const initialValues = useMemo(() => getInitialValues(activity), [activity]);

  const [selectedTags, setSelectedTags] = useState(initialValues.tags);
  const [title, setTitle] = useState(initialValues.title);
  const [note, setNote] = useState(initialValues.note);
  const [assetDeleted, setAssetDeleted] = useState<boolean>(false);
  const [assetLoaded, setAssetLoaded] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [incorrectFileFormat, setIncorrectFileFormat] = useState<boolean>(false);
  const [permanent, setPermanent] = useState<boolean>(initialValues.permanent);
  const [color, setColor] = useState<ActivityColor>(initialValues.color);
  const [date, setDate] = useState<string>(() => initialValues.date || moment()
    .set('year', parseInt(currentSeasonYear, 10))
    .format('YYYY-MM-DD'));

  const isNewComment = initialValues.note === '';

  const [showTitleErrorMessage, setShowTitleErrorMessage] = useState(false);

  const onCloseModal = useCallback(() => {
    dispatch(closeModal(index));
    dispatch(setSelectedActivity(index, null));
  }, [dispatch, index]);

  const handleCloseConfirmationClick = () => {
    if (initialValues.id) {
      onCloseModal();
    } else {
      dispatch(resizeModal(ModalSize.S));
      setCloseConfirmationModal(true);
    }
  };

  const onCancelGoBack = () => {
    dispatch(resizeModal(ModalSize.L));
    setCloseConfirmationModal(false);
  };

  const goBack = () => {
    dispatch(openModal(index, null as never, {
      type: REGION_REPORT,
      size: ModalSize.L,
      activity: activity ?? {
        geometry: region,
      },
      mode: modalMode,
    }));
  };

  const removeAllComments = () => {
    if (activity && activity.id && activity.comments.length > 0) {
      const { id, comments } = activity;
      const commentsIds = comments.map(comment => comment.id);

      dispatch(startDeleteComments());
      dispatch(reqDeleteComments(id, commentsIds, index));
    }
  };

  const assetClass = classNames(styles.asset, {
    [styles.hiddenAsset]: !assetLoaded,
  });

  const isLoading = isLoadingActivity || isLoadingUpdatedActivity;

  const trackTagsEvent = activitySave => {
    const removedTags = difference(initialValues.tags, activitySave.tags);
    const addedTags = difference(activitySave.tags, initialValues.tags);

    if (removedTags.length > 0) {
      dispatch(
        trackEvent('region_note_tag', 'removed', {
          regionNoteUuid: activitySave.id,
          selectedTags: removedTags,
        })
      );
    }
    if (addedTags.length > 0) {
      dispatch(
        trackEvent('region_note_tag', 'added', {
          regionNoteUuid: activitySave.id,
          selectedTags: addedTags,
        })
      );
    }
  };

  const updateActivity = (activitySave, scoutedAt) => {
    if (!activity) {
      return;
    }
    const comment = activity.comments.length > 0 ? activity.comments[0] : {} as ActivityComment;

    if (assetDeleted) {
      dispatch(reqDeleteAsset({
        activityId: activity.id,
        assetId: activity.assets[0].id,
      }));
    }

    dispatch(
      reqUpdateActivity(
        activitySave,
        { ...comment, note },
        {
          oldAsset: !assetDeleted && activity.assets[0],
          newAsset: file,
        },
        {
          from: getActivitySeasonYear(activity, activity.scoutedAt),
          to: getActivitySeasonYear(activity, scoutedAt),
        },
        isNewComment
      )
    );
  };

  const onSaveButtonClick = () => {
    if (title === undefined || title.trim() === '') {
      // if the title is empty, show the error message and don't save the new FRR.
      setShowTitleErrorMessage(true);
      return;
    }

    const scoutedAt = moment(date).toISOString();
    const activitySave: Partial<Activity> & { index: number } = {
      title,
      comments: [{ note }] as unknown as ActivityComment[],
      color,
      field: field.uuid,
      permanent,
      scoutedAt,
      index,
      tags: mapTags(tagOptions, selectedTags),
      location: activity?.location ?? region,
    };

    activitySave.id = activity?.id;
    if (modalMode === ModalMode.EDIT_REGION) {
      updateActivity(activitySave, scoutedAt);
      trackTagsEvent(activitySave);
    } else {
      dispatch(
        reqNewActivity(activitySave, file)
      );
    }
  };

  const confirmTitleKey = 'regions.report.modal.delete';

  const confirmFooterKey = 'scouting.form.delete.note';

  const onChangeFile = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return;
    }

    const uploadedFile = e.target.files[0];

    if (uploadedFile) {
      setFile(uploadedFile);
      setIncorrectFileFormat(!ASSET_ACCEPTED_TYPES.includes(uploadedFile.type));
    }
  };

  const onClickAssetDelete = () => {
    if (fieldHasScoutingAddPhotoFeature) {
      setAssetDeleted(true);
    } else {
      setRemovePhotoConfirmationModal(true);
    }
  }

  const onConfirmRemovePhoto = () => {
    setAssetDeleted(true);
    dispatch(resizeModal(ModalSize.L));
    setRemovePhotoConfirmationModal(false);
  };

  if (deleteConfirmationModal) {
    return (
      <DeleteConfirmationModal
        confirmTitleKey={confirmTitleKey}
        removeAllComments={removeAllComments}
        isLoading={isLoading}
        handleClickCancel={onCloseModal}
      />
    );
  }

  if (removePhotoConfirmationModal) {
    return (
      <RemovePhotoConfirmationModal
        confirmTitleKey={confirmTitleKey}
        onClickCancel={onConfirmRemovePhoto}
        onCancelGoBack={() => {
          dispatch(resizeModal(ModalSize.L));
          setRemovePhotoConfirmationModal(false);
        }}
      />
    );
  }

  if (closeConfirmationModal) {
    return (
      <CloseConfirmationModal
        onCancelGoBack={onCancelGoBack}
        handleClickCancel={onCloseModal}
      />
    );
  }

  return (
    <Dialog
      ref={wrapperRef}
      testId='region-note-form'
      key='region-note-form'
      defaultOpen
      open
      isModal
      width={DialogWidth.Wide}
      onOpenChange={handleCloseConfirmationClick}
      closeOnOutsideClick={false}
      headerProps={{
        showCloseButton: true,
        title: (
          <Container
              width='100%'
              orientation={Orientation.Horizontal}>
                <Button
                  fill={ButtonFill.Ghost}
                  iconOnly
                  width={ButtonWidth.FitContent}
                  onClick={goBack}
                  color={ComponentColor.Primary}
                  testId="region-note-form-button-back"
                  aria-label={getMessage('confirmationModal.goBack')}
                  size={ComponentSize.Sm}>
                  <Icon icon={FdsIcon.ArrowBack} />
                </Button>

              <Typography tag="h2" variant={TypographyVariant.Subheading}>
                {getMessage('regions.report.modal.title')}
              </Typography>
            </Container>
        ),
      }}>
      <DialogContent>
        <div className={styles.regionNoteForm}>
          <RegionNoteFormTitle
            title={title}
            setTitle={setTitle}
            showTitleErrorMessage={showTitleErrorMessage}
          />
          <RegionNoteFormComment
            note={note}
            setNote={setNote}
          />
          <RegionNoteFormFile
            activity={activity}
            assetDeleted={assetDeleted}
            onClickAssetDelete={onClickAssetDelete}
            file={file}
            setFile={setFile}
            incorrectFileFormat={incorrectFileFormat}
            setIncorrectFileFormat={setIncorrectFileFormat}
            assetLoaded={assetLoaded}
            setAssetLoaded={setAssetLoaded}
            onChangeFile={onChangeFile}
            assetClass={assetClass}
          />
          <RegionNoteFormType
            permanent={permanent}
            setPermanent={setPermanent}
          />
          <RegionNoteFormColor
            color={color}
            setColor={setColor}
          />
          <RegionNoteFormDate
            currentSeasonYear={currentSeasonYear}
            date={date}
            setDate={setDate}
          />
          <RegionNoteFormTags
            activity={activity}
            selectedTags={selectedTags}
            setSelectedTags={setSelectedTags}
          />
        </div>
        <DialogFooter padding={ComponentPadding.None} gap={DensitySpacing.Together}>
          <RegionNoteFormFooter
            modalMode={modalMode}
            isLoading={isLoading}
            confirmTitleKey={confirmFooterKey}
            setDeleteConfirmationModal={setDeleteConfirmationModal}
            handleCloseConfirmationClick={handleCloseConfirmationClick}
            onSaveButtonClick={onSaveButtonClick}
            incorrectFileFormat={incorrectFileFormat}
            isNewComment={isNewComment}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
